import React, { useState } from 'react';
import api, { API_URL } from '../../services/api';
import { Truck, Save, CheckCircle, X, UserPlus } from 'lucide-react';

// Actions par piece depot, PARTAGEES entre la vue a plat (Depots.js) et le detail depot
// (DepotDetail.js). 12/06/2026. Garde-fous identiques au backend :
//  - Vendre : anti-A17 (reste impaye -> client reel + "mettre en compte")
//  - Reaffecter : refuse cible=source / piece vendue (backend) ; + option "nouveau deposant"
//  - Editer prix : refuse piece vendue + 0 <= du <= pvp
// piece normalisee attendue : { id, id_produit, reference_origine, nom, pvp, prix_net_deposant,
//   inventaire_actif, depot_id, numero_depot, deposant, montant_du_deposant, montant_verse_deposant }
export default function ActionsPiece({ piece, depots = [], clients = [], onDone, fmt }) {
  const [modal, setModal] = useState(null);   // 'reaffect' | 'prix' | 'vendre'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Action 2 : reaffecter
  const [reaffectMode, setReaffectMode] = useState('existant'); // 'existant' | 'nouveau'
  const [reaffectCible, setReaffectCible] = useState('');
  const [newDep, setNewDep] = useState({ nom: '', telephone: '' });

  // Action 3 : prix
  const [prixForm, setPrixForm] = useState({ pvp: '', prix_net_deposant: '' });

  // Action 1 : vendre
  const [vf, setVf] = useState(null);

  const close = (msg) => { setModal(null); setError(''); if (onDone) onDone(msg); };

  const openReaffect = () => { setReaffectMode('existant'); setReaffectCible(''); setNewDep({ nom: '', telephone: '' }); setError(''); setModal('reaffect'); };
  const openPrix = () => { setPrixForm({ pvp: piece.pvp != null ? String(piece.pvp) : '', prix_net_deposant: piece.prix_net_deposant != null ? String(piece.prix_net_deposant) : '' }); setError(''); setModal('prix'); };
  const openVendre = () => {
    setVf({
      client_id: '', client_nom: '', prix_vente: piece.pvp != null ? String(piece.pvp) : '', remise_montant: '',
      p_especes: '', p_cheque: '', p_cheque_ref: '', p_carte: '', p_virement: '',
      p_or_poids: '', p_or_prix_g: '', mettre_en_compte: false, avoir_client: false,
      type_caisse: 'officielle', observation: '',
    });
    setError(''); setModal('vendre');
  };

  // ── Action 2 : reaffecter (depot existant OU nouveau deposant a la volee) ──
  const doReaffect = async () => {
    setBusy(true); setError('');
    try {
      let cibleId;
      if (reaffectMode === 'nouveau') {
        if (!newDep.nom.trim()) { setError('Nom du nouveau déposant requis'); setBusy(false); return; }
        const boutiqueId = depots.find(d => d.id === piece.depot_id)?.boutique_id || depots[0]?.boutique_id;
        if (!boutiqueId) { setError('Boutique introuvable pour créer le dépôt'); setBusy(false); return; }
        const cli = await api.post(`${API_URL}/clients`, { nom: newDep.nom.trim(), telephone: newDep.telephone || undefined });
        const dep = await api.post(`${API_URL}/depots`, { boutique_id: boutiqueId, modalite: 'DEPOT_VENTE', client_id: cli.data.id });
        cibleId = dep.data.id;
      } else {
        if (!reaffectCible) { setError('Choisir un dépôt cible'); setBusy(false); return; }
        cibleId = parseInt(reaffectCible);
      }
      const r = await api.put(`${API_URL}/depots/bijoux/${piece.id}/reaffecter`, { depot_id_cible: cibleId });
      close(`Pièce ${piece.reference_origine} → ${r.data.deposant} (${r.data.numero_depot})`);
    } catch (err) { setError(err.response?.data?.error || 'Erreur réaffectation'); }
    finally { setBusy(false); }
  };

  // ── Action 3 : editer les prix ──
  const doEditPrix = async () => {
    const body = {};
    if (prixForm.pvp !== '') body.pvp = parseFloat(prixForm.pvp);
    if (prixForm.prix_net_deposant !== '') body.prix_net_deposant = parseFloat(prixForm.prix_net_deposant);
    if (body.pvp == null && body.prix_net_deposant == null) { setError('Renseigner au moins un prix'); return; }
    setBusy(true); setError('');
    try {
      await api.put(`${API_URL}/depots/bijoux/${piece.id}/prix`, body);
      close(`Prix mis à jour : ${piece.reference_origine}`);
    } catch (err) { setError(err.response?.data?.error || 'Erreur édition prix'); }
    finally { setBusy(false); }
  };

  // ── Action 1 : vendre (multi-modes, calque VenteBoutique) ──
  const vfNum = (k) => parseFloat((vf && vf[k]) || 0) || 0;
  const sousTotal = Math.max(0, vfNum('prix_vente') - vfNum('remise_montant'));
  const orMontant = (vfNum('p_or_poids') > 0 && vfNum('p_or_prix_g') > 0) ? vfNum('p_or_poids') * vfNum('p_or_prix_g') : 0;
  const paye = vfNum('p_especes') + vfNum('p_cheque') + vfNum('p_carte') + vfNum('p_virement') + orMontant;
  const reste = Math.max(0, +(sousTotal - paye).toFixed(2));
  const tropPercu = Math.max(0, +(paye - sousTotal).toFixed(2));

  const doVendre = async () => {
    if (!vf.client_id && !vf.client_nom.trim()) { setError('Sélectionnez un client (ou saisissez un nom)'); return; }
    if (vfNum('prix_vente') <= 0) { setError('Prix de vente requis'); return; }
    const paiements = [];
    if (vfNum('p_especes') > 0) paiements.push({ mode: 'especes', montant: vfNum('p_especes') });
    if (vfNum('p_cheque') > 0) paiements.push({ mode: 'cheque', montant: vfNum('p_cheque'), reference: vf.p_cheque_ref || '' });
    if (vfNum('p_carte') > 0) paiements.push({ mode: 'carte', montant: vfNum('p_carte') });
    if (vfNum('p_virement') > 0) paiements.push({ mode: 'virement', montant: vfNum('p_virement') });
    if (orMontant > 0) paiements.push({ mode: 'or', montant: orMontant, or_poids: vfNum('p_or_poids'), or_prix_g: vfNum('p_or_prix_g') });

    const clientId = vf.client_id || undefined;
    if (reste > 0.01) {
      if (!clientId) { setError(`Paiement incomplet (reste ${fmt(reste)}) : choisissez un CLIENT existant pour mettre le reste en compte, ou complétez le paiement.`); return; }
      if (!vf.mettre_en_compte) { setError(`Reste ${fmt(reste)} non réglé : cochez « mettre le reste en compte » ou complétez le paiement.`); return; }
      paiements.push({ mode: 'credit', montant: -reste });
    }
    if (tropPercu > 0.01 && vf.avoir_client && clientId) paiements.push({ mode: 'avoir', montant: -tropPercu });

    const modesPositifs = paiements.filter(p => p.montant > 0);
    const modePaiement = modesPositifs.length > 1 ? 'multiple' : modesPositifs.length === 1 ? modesPositifs[0].mode : 'especes';

    setBusy(true); setError('');
    try {
      const r = await api.post(`${API_URL}/bijoux/catalogue/pieces/${piece.id_produit}/vendre`, {
        client_id: clientId,
        client_nom: vf.client_nom.trim() || undefined,
        prix_vente: vfNum('prix_vente'),
        remise_montant: vfNum('remise_montant') > 0 ? vfNum('remise_montant') : undefined,
        montant_final: sousTotal,
        mode_paiement: modePaiement,
        type_caisse: vf.type_caisse,
        observation: vf.observation || undefined,
        paiements: paiements.length > 0 ? paiements : undefined,
      });
      close(`Vente enregistrée (${r.data.num_facture})${r.data.warning === 'caisse_fermee' ? ' — ⚠ CAISSE FERMÉE, pensez au fond de caisse' : ''}`);
    } catch (err) { setError(err.response?.data?.error || 'Erreur vente'); }
    finally { setBusy(false); }
  };

  const Err = () => error ? <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{error}</div> : null;

  // Pieces vendues : pas d'actions
  if (!piece.inventaire_actif) return <span className="text-gray-300 text-xs">vendu</span>;

  return (
    <>
      <div className="flex items-center justify-center gap-1">
        <button onClick={openReaffect} title="Changer le déposant" className="px-1.5 py-1 rounded text-indigo-600 hover:bg-indigo-50"><Truck className="h-4 w-4" /></button>
        <button onClick={openPrix} title="Éditer les prix" className="px-1.5 py-1 rounded text-amber-600 hover:bg-amber-50"><Save className="h-4 w-4" /></button>
        <button onClick={openVendre} title="Vendre" className="px-2 py-1 rounded bg-green-600 text-white text-xs font-semibold hover:bg-green-700">Vendre</button>
      </div>

      {/* ── Changer le déposant ── */}
      {modal === 'reaffect' && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] overflow-y-auto">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-indigo-500 to-indigo-700 text-white px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h3 className="text-lg font-semibold">Changer le déposant</h3>
              <button onClick={() => setModal(null)} className="hover:bg-white/20 p-1 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
                <p><span className="font-bold">Pièce :</span> <span className="font-mono text-purple-700">{piece.reference_origine}</span> — {piece.nom}</p>
                <p><span className="font-bold">Déposant actuel :</span> {piece.deposant} ({piece.numero_depot || '—'})</p>
                <p className="text-xs text-gray-500 mt-1">Le dû déposant ({fmt(piece.prix_net_deposant || 0)}) suit la pièce, inchangé.</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setReaffectMode('existant')} className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium ${reaffectMode === 'existant' ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-white border-gray-300 text-gray-600'}`}>Dépôt existant</button>
                <button onClick={() => setReaffectMode('nouveau')} className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium flex items-center justify-center gap-1 ${reaffectMode === 'nouveau' ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-white border-gray-300 text-gray-600'}`}><UserPlus className="h-4 w-4" />Nouveau déposant</button>
              </div>
              {reaffectMode === 'existant' ? (
                <select value={reaffectCible} onChange={e => setReaffectCible(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                  <option value="">-- Choisir un dépôt --</option>
                  {depots.filter(d => d.modalite === 'DEPOT_VENTE' && d.id !== piece.depot_id).map(d => (
                    <option key={d.id} value={d.id}>{d.numero_depot} — {d.fournisseur_nom || d.client_nom || d.deposant_nom || 'Déposant'}</option>
                  ))}
                </select>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" placeholder="Nom du déposant *" value={newDep.nom} onChange={e => setNewDep({ ...newDep, nom: e.target.value })} className="px-3 py-2 border rounded-lg" />
                  <input type="text" placeholder="Téléphone" value={newDep.telephone} onChange={e => setNewDep({ ...newDep, telephone: e.target.value })} className="px-3 py-2 border rounded-lg" />
                  <p className="col-span-2 text-xs text-gray-400">Crée le client + un nouveau dépôt rattaché, puis y déplace la pièce.</p>
                </div>
              )}
              <Err />
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end gap-3">
              <button onClick={() => setModal(null)} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg">Annuler</button>
              <button onClick={doReaffect} disabled={busy} className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"><Truck className="h-4 w-4" />{busy ? '...' : 'Réaffecter'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Éditer les prix ── */}
      {modal === 'prix' && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] overflow-y-auto">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h3 className="text-lg font-semibold">Éditer les prix</h3>
              <button onClick={() => setModal(null)} className="hover:bg-white/20 p-1 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700"><span className="font-mono text-purple-700">{piece.reference_origine}</span> — {piece.nom}</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">PVP magasin</label>
                  <input type="text" inputMode="decimal" value={prixForm.pvp} onChange={e => setPrixForm({ ...prixForm, pvp: e.target.value })} onFocus={e => e.target.select()} className="w-full px-3 py-2 border rounded-lg font-bold" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Dû déposant</label>
                  <input type="text" inputMode="decimal" value={prixForm.prix_net_deposant} onChange={e => setPrixForm({ ...prixForm, prix_net_deposant: e.target.value })} onFocus={e => e.target.select()} className="w-full px-3 py-2 border rounded-lg font-bold" placeholder="à saisir" />
                </div>
              </div>
              <p className="text-xs text-gray-500">Le dû ne peut pas dépasser le PVP. Recale le montant dû. (Pièce vendue = édition bloquée.)</p>
              <Err />
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end gap-3">
              <button onClick={() => setModal(null)} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg">Annuler</button>
              <button onClick={doEditPrix} disabled={busy} className="px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"><Save className="h-4 w-4" />{busy ? '...' : 'Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Vendre (multi-modes) ── */}
      {modal === 'vendre' && vf && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] overflow-y-auto">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-green-500 to-emerald-700 text-white px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h3 className="text-lg font-semibold">Vendre la pièce</h3>
              <button onClick={() => setModal(null)} className="hover:bg-white/20 p-1 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
                <span className="font-mono text-purple-700">{piece.reference_origine}</span> — {piece.nom}
                <p className="text-xs text-gray-500 mt-1">Dépôt {piece.numero_depot || '—'} · dû déposant {fmt(piece.prix_net_deposant || 0)} (sera mis en attente de règlement).</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Client (existant)</label>
                  <select value={vf.client_id} onChange={e => setVf({ ...vf, client_id: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">-- Choisir --</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.nom}{c.telephone ? ` (${c.telephone})` : ''}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">ou nouveau client (nom)</label>
                  <input type="text" value={vf.client_nom} onChange={e => setVf({ ...vf, client_nom: e.target.value })} className="w-full px-3 py-2 border rounded-lg" placeholder="Nom du client" disabled={!!vf.client_id} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Prix de vente *</label>
                  <input type="text" inputMode="decimal" value={vf.prix_vente} onChange={e => setVf({ ...vf, prix_vente: e.target.value })} onFocus={e => e.target.select()} className="w-full px-3 py-2 border rounded-lg font-bold" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Remise (montant)</label>
                  <input type="text" inputMode="decimal" value={vf.remise_montant} onChange={e => setVf({ ...vf, remise_montant: e.target.value })} onFocus={e => e.target.select()} className="w-full px-3 py-2 border rounded-lg" placeholder="0" />
                </div>
              </div>
              <div className="border rounded-lg p-3 space-y-2 bg-gray-50/50">
                <p className="text-xs font-bold text-gray-600 uppercase">Règlement</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="block text-xs text-gray-500 mb-0.5">Espèces</label><input type="text" inputMode="decimal" value={vf.p_especes} onChange={e => setVf({ ...vf, p_especes: e.target.value })} onFocus={e => e.target.select()} className="w-full px-2 py-1.5 border rounded text-sm" placeholder="0" /></div>
                  <div><label className="block text-xs text-gray-500 mb-0.5">Carte</label><input type="text" inputMode="decimal" value={vf.p_carte} onChange={e => setVf({ ...vf, p_carte: e.target.value })} onFocus={e => e.target.select()} className="w-full px-2 py-1.5 border rounded text-sm" placeholder="0" /></div>
                  <div><label className="block text-xs text-gray-500 mb-0.5">Virement</label><input type="text" inputMode="decimal" value={vf.p_virement} onChange={e => setVf({ ...vf, p_virement: e.target.value })} onFocus={e => e.target.select()} className="w-full px-2 py-1.5 border rounded text-sm" placeholder="0" /></div>
                  <div><label className="block text-xs text-gray-500 mb-0.5">Chèque</label><input type="text" inputMode="decimal" value={vf.p_cheque} onChange={e => setVf({ ...vf, p_cheque: e.target.value })} onFocus={e => e.target.select()} className="w-full px-2 py-1.5 border rounded text-sm" placeholder="0" /></div>
                </div>
                {vfNum('p_cheque') > 0 && <input type="text" value={vf.p_cheque_ref} onChange={e => setVf({ ...vf, p_cheque_ref: e.target.value })} className="w-full px-2 py-1.5 border rounded text-sm" placeholder="N° / réf chèque" />}
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="block text-xs text-gray-500 mb-0.5">Or repris (g)</label><input type="text" inputMode="decimal" value={vf.p_or_poids} onChange={e => setVf({ ...vf, p_or_poids: e.target.value })} onFocus={e => e.target.select()} className="w-full px-2 py-1.5 border rounded text-sm" placeholder="0" /></div>
                  <div><label className="block text-xs text-gray-500 mb-0.5">Prix or / g</label><input type="text" inputMode="decimal" value={vf.p_or_prix_g} onChange={e => setVf({ ...vf, p_or_prix_g: e.target.value })} onFocus={e => e.target.select()} className="w-full px-2 py-1.5 border rounded text-sm" placeholder="0" /></div>
                </div>
                <div className="text-xs text-gray-600 border-t pt-2 space-y-0.5">
                  <div className="flex justify-between"><span>Net à payer</span><span className="font-bold">{fmt(sousTotal)}</span></div>
                  <div className="flex justify-between"><span>Payé</span><span>{fmt(paye)}</span></div>
                  {reste > 0.01 && <div className="flex justify-between text-amber-700 font-semibold"><span>Reste</span><span>{fmt(reste)}</span></div>}
                  {tropPercu > 0.01 && <div className="flex justify-between text-blue-700 font-semibold"><span>Trop-perçu</span><span>{fmt(tropPercu)}</span></div>}
                </div>
                {reste > 0.01 && (
                  <label className="flex items-center gap-2 text-xs text-gray-700">
                    <input type="checkbox" checked={vf.mettre_en_compte} onChange={e => setVf({ ...vf, mettre_en_compte: e.target.checked })} />
                    Mettre le reste ({fmt(reste)}) en compte client {!vf.client_id && <span className="text-red-500">(client existant requis)</span>}
                  </label>
                )}
                {tropPercu > 0.01 && (
                  <label className="flex items-center gap-2 text-xs text-gray-700">
                    <input type="checkbox" checked={vf.avoir_client} onChange={e => setVf({ ...vf, avoir_client: e.target.checked })} disabled={!vf.client_id} />
                    Créditer le trop-perçu ({fmt(tropPercu)}) en avoir client
                  </label>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Type caisse</label>
                  <select value={vf.type_caisse} onChange={e => setVf({ ...vf, type_caisse: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                    <option value="officielle">Officielle</option>
                    <option value="instance">Instance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Observation</label>
                  <input type="text" value={vf.observation} onChange={e => setVf({ ...vf, observation: e.target.value })} className="w-full px-3 py-2 border rounded-lg" placeholder="(optionnel)" />
                </div>
              </div>
              <Err />
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end gap-3">
              <button onClick={() => setModal(null)} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg">Annuler</button>
              <button onClick={doVendre} disabled={busy} className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"><CheckCircle className="h-4 w-4" />{busy ? 'Vente...' : 'Vendre'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
